import React, { useEffect, useReducer, useState } from 'react'

import { Snackbar } from '@material-ui/core'
import { Alert } from '@mui/material'
import { GridPinnedColumns, GridSortItem } from '@mui/x-data-grid-pro'

import api from 'common/api'
import { UIFilterConverters } from 'routes/highschools/filter-state-to-url-params'
import { PageSizeOption, SortKey, UIFilters } from 'routes/highschools/types'
import { useAppSelector } from 'store'

import { standardViews } from './defaultViews'
import {
  ViewQueryState,
  ViewQueryStateConverters,
  convertViewQueryStateToUIFilters,
  decodeViewsQueryParams,
} from './filter-params'
import { CreateHSExploreView, HSExploreView, HSExploreViews } from './types'
import useSyncViewState from './useSyncViewState'
import { ViewsContext, normalizeOrderModel } from './useViews'
import viewsReducer, { initialState } from './viewsReducer'

const createViewFromQueryState = (
  state: ViewQueryState,
  defaultView: HSExploreView,
  hasUrlChanges: boolean,
  isPremiumExplorerEnabled: boolean,
): HSExploreView => {
  const filterModel = convertViewQueryStateToUIFilters(state, isPremiumExplorerEnabled)
  const validFields = Object.keys(standardViews.views[0].visibilityModel)
  const field = state.sort ?? defaultView?.sortModel?.field
  const validField = field && validFields.includes(field) ? field : 'name'
  const view = {
    ...defaultView,
    filterModel: hasUrlChanges ? filterModel : defaultView.filterModel,
    sortModel: {
      sort: state.sortDir ?? defaultView?.sortModel?.sort,
      field: validField,
    },
  }

  return view
}

const normalizeAPIFilterModel = (filterModel: UIFilters): UIFilters => {
  /* TODO: We need to manually decode our visitrange so we get a Date object like UIFilters expects. 
+            We should improve this with better validation. See https://scoir-dev.atlassian.net/jira/software/projects/HE/boards/82?selectedIssue=HE-184 
+            */
  return {
    ...filterModel,
    ...(filterModel?.visitRange && {
      visitRange: UIFilterConverters.visitRange.decode([
        filterModel.visitRange.type === 'custom'
          ? `${filterModel.visitRange.startDate || ''};${filterModel.visitRange.endDate || ''}`
          : filterModel.visitRange.type,
      ]),
    }),
  }
}
const getViewWithDefaults = (view: HSExploreView): HSExploreView => {
  const validFields = Object.keys(standardViews.views[0].visibilityModel)
  const sortField = view.sortModel?.field
  const validField = sortField && validFields.includes(sortField) ? sortField : 'name'
  return {
    ...view,
    visibilityModel: { ...standardViews.views[0].visibilityModel, ...(view.visibilityModel ?? {}) },
    orderModel: normalizeOrderModel({ ...standardViews.views[0].orderModel, ...(view.orderModel ?? {}) }),
    sortModel: { sort: view.sortModel?.sort ?? 'desc', field: validField },
    pinnedModel: view.pinnedModel || {
      left: [],
      right: [],
    },
    widthModel: view.widthModel || {},
    filterModel: normalizeAPIFilterModel(view.filterModel),
    columnConfigModel: view.columnConfigModel ?? {},
  }
}

interface ViewInfo {
  currentPage: number
  activeView: HSExploreView
  pageSize: PageSizeOption
  defaultViewId: string
  views: HSExploreView[]
}
const resolveCurrentViewState = (
  apiViews: HSExploreViews,
  urlViewParams: URLSearchParams,
  isPremiumExplorerEnabled: boolean,
  sessionViewState?: HSExploreView,
): ViewInfo => {
  const viewsWithDefault = apiViews.views.map((s) => {
    return getViewWithDefaults(s)
  })
  const viewUrlState = decodeViewsQueryParams(urlViewParams)
  const defaultView = viewsWithDefault.find((s) => s.id === apiViews.defaultViewId) || standardViews.views[0]
  let hasUrlParams = false
  const uiFilterKeys = Object.keys(ViewQueryStateConverters)
  for (const key of urlViewParams.keys()) {
    if (uiFilterKeys.includes(key)) {
      hasUrlParams = true
      break
    }
  }
  const view = createViewFromQueryState(
    viewUrlState,
    sessionViewState ? getViewWithDefaults(sessionViewState) : defaultView,
    hasUrlParams,
    isPremiumExplorerEnabled,
  )

  return {
    currentPage: viewUrlState.currentPage ? viewUrlState.currentPage : 0,
    views: [...standardViews.views, ...viewsWithDefault],
    activeView: { ...defaultView, ...view },
    defaultViewId: apiViews.defaultViewId || standardViews.views[0].id,
    pageSize: viewUrlState.pageSize ?? 25,
  }
}

export function ViewsProvider({ children }: { children: React.ReactNode }) {
  const { urlState, sessionView, syncStateToUrl } = useSyncViewState()

  const [state, dispatch] = useReducer(viewsReducer, initialState)
  const isPremiumExplorerEnabled = useAppSelector(
    (state) => state.college.record?.Features?.SupportsPremiumHighSchoolExplorer || false,
  )
  useEffect(() => {
    if (state.isLoading) return
    const newUrlState: ViewQueryState = {
      ...state.activeView.filterModel,
      ...(state.activeView.sortModel?.sort && { sortDir: state.activeView.sortModel.sort }),
      ...(state.activeView.sortModel?.field && { sort: state.activeView.sortModel.field as SortKey }),
      currentPage: state.currentPage,
      pageSize: state.pageSize,
    }
    syncStateToUrl(newUrlState, state.activeView)
  }, [state.activeView, state.currentPage, state.isLoading, syncStateToUrl])
  const [notification, setNotification] = useState<{
    message: string
    severity: 'success' | 'error'
    open: boolean
  }>({
    message: '',
    severity: 'success',
    open: false,
  })

  const showNotification = (message: string, severity: 'success' | 'error') => {
    setNotification({
      message,
      severity,
      open: true,
    })
  }

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }))
  }

  useEffect(() => {
    const { activeView, currentPage, pageSize, defaultViewId, views } = resolveCurrentViewState(
      standardViews,
      urlState,
      isPremiumExplorerEnabled,
      sessionView,
    )
    const loadViews = async () => {
      try {
        const response = await api.get<HSExploreViews>('/api/highschools/explore_views')
        const { activeView, currentPage, pageSize, defaultViewId, views } = resolveCurrentViewState(
          response,
          urlState,
          isPremiumExplorerEnabled,
          sessionView,
        )
        dispatch({
          type: 'load-views',
          data: {
            currentPage: currentPage,
            views: views,
            activeView: activeView,
            defaultViewId: defaultViewId,
            pageSize: pageSize,
          },
        })
      } catch {
        dispatch({
          type: 'load-views',
          data: {
            views: views,
            defaultViewId: defaultViewId,
            activeView: activeView,
            currentPage: currentPage,
            pageSize: pageSize,
          },
        })
        dispatch({ type: 'set-error', hasError: true })
      }
    }
    if (isPremiumExplorerEnabled) {
      loadViews()
    } else {
      let view = activeView
      let newDefaultId = defaultViewId
      if (defaultViewId !== standardViews.views[0].id) {
        view = standardViews.views[0]
        newDefaultId = view.id
      }
      dispatch({
        type: 'load-views',
        data: {
          views: views,
          defaultViewId: newDefaultId,
          activeView: view,
          currentPage: currentPage,
          pageSize: pageSize,
        },
      })
    }
  }, [])

  const changeActiveView = (view: HSExploreView) => {
    dispatch({ type: 'set-active-view', view })
  }

  const updateColumnVisibility = (visibilityModel: { [key: string]: boolean }) => {
    dispatch({ type: 'update-column-visibility', visibilityModel })
  }

  const updatePinnedColumns = (pinnedColumns: GridPinnedColumns) => {
    dispatch({ type: 'update-pinned-columns', pinnedColumns })
  }

  const updateColumnOrder = (orderModel: { [key: string]: number }) => {
    dispatch({ type: 'update-column-order', orderModel })
  }
  const updateColumnSort = (sortModel: GridSortItem) => {
    dispatch({ type: 'update-column-sort', sortModel })
  }

  const updateColumnWidth = (widthModel: { [key: string]: number }) => {
    dispatch({ type: 'update-column-width', widthModel })
  }

  const updatePageSize = (pageSize: PageSizeOption) => {
    dispatch({ type: 'pageSize-change', pageSize })
  }

  const updateCurrentPage = (page: number) => {
    dispatch({ type: 'page-change', page })
  }

  const createView = async (view: CreateHSExploreView) => {
    try {
      const savedView = await api.post<Omit<CreateHSExploreView, 'pageSize'>, HSExploreView>(
        '/api/highschools/explore_views',
        view,
      )

      const normalizedFilterModel = normalizeAPIFilterModel(savedView.filterModel)
      const newView: HSExploreView = {
        ...savedView,
        filterModel: normalizedFilterModel,
      }

      dispatch({ type: 'create-view', view: newView, isDefault: view.isDefault || false })
      showNotification('View created successfully', 'success')
      return savedView
    } catch (error) {
      dispatch({ type: 'set-error', hasError: true })
      showNotification('Unable to create view', 'error')
      throw error
    }
  }

  const updateColumnFilters = (filters: UIFilters) => {
    dispatch({ type: 'update-column-filters', filters })
  }

  const updateColumnConfig = (field: string, config: Record<string, unknown>) => {
    dispatch({ type: 'update-column-config', field, config })
  }

  const updateView = async (view: HSExploreView) => {
    try {
      await api.put(`/api/highschools/explore_views`, view)
      dispatch({ type: 'update-view', view })
      showNotification('View updated successfully', 'success')
    } catch (error) {
      dispatch({ type: 'set-error', hasError: true })
      showNotification('Unable to update view', 'error')
      throw error
    }
  }

  const deleteView = async (viewId: string) => {
    try {
      await api.delete(`/api/highschools/explore_views/${viewId}`)
      dispatch({ type: 'delete-view', viewId })
      showNotification('View deleted successfully', 'success')
    } catch (error) {
      dispatch({ type: 'set-error', hasError: true })
      showNotification('Unable to delete view', 'error')
      throw error
    }
  }

  const updateDefaultView = async (viewId: string) => {
    try {
      await api.post(`/api/highschools/explore_views_default`, { id: viewId })
      dispatch({ type: 'update-default-view', id: viewId })
      showNotification('View updated successfully', 'success')
    } catch (error) {
      dispatch({ type: 'set-error', hasError: true })
      showNotification('Unable to update view', 'error')
      throw error
    }
  }

  const value = {
    views: state.viewData,
    isLoading: state.isLoading,
    activeView: state.activeView,
    currentPage: state.currentPage,
    pageSize: state.pageSize,
    changeActiveView,
    updateColumnVisibility,
    updateColumnOrder,
    createView,
    updateView,
    deleteView,
    updatePinnedColumns,
    updateColumnWidth,
    updateDefaultView,
    updateColumnSort,
    updatePageSize,
    updateCurrentPage,
    updateColumnFilters,
    updateColumnConfig,
  }

  return (
    <ViewsContext.Provider value={value}>
      {!state.isLoading && children}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </ViewsContext.Provider>
  )
}
